import type { IJob, Job } from "./useJobs";
import type { ISummaryData } from "@/types/summary";
import { SummaryData } from "@/types/summary";
import { useJob } from "./useJob";
import { useSummary } from "./useSummary";
import { useCharacters } from "./useCharacters";
import { useParagraphHistories } from "./useParagraphHistories";

const serializeJob = (job: Job): IJob => ({
  jobId: job.jobId,
  userId: job.userId,
  manuscriptUri: job.manuscriptUri,
  fileName: job.fileName,
  summaryUri: job.summaryUri,
  uploadedAt: job.uploadedAt,
  status: job.status,
  summaryId: job.summaryId,
  desiredWordCount: job.desiredWordCount,
  outputLanguage: job.outputLanguage,
  markedForDeletion: job.markedForDeletion,
  tenantId: job.tenantId,
  displayStatus: job.displayStatus,
});

export interface IJobDetailsData {
  job: IJob | null;
  summaryData: ISummaryData | null;
  isLoading: boolean;
  error: Error | null;
}

export const useJobDetailsData = (jobId: string): IJobDetailsData => {
  const {
    job: jobInstance,
    isLoading: jobLoading,
    error: jobError,
  } = useJob(jobId);
  const summaryId = jobInstance?.summaryId ?? null;

  const {
    factSheetWithoutCharacters,
    isLoading: summaryLoading,
    error: factSheetWithoutCharactersError,
  } = useSummary(summaryId);
  const {
    paragraphs,
    isLoading: paragraphsLoading,
    error: paragraphsError,
  } = useParagraphHistories(summaryId);
  const {
    characters,
    isLoading: charactersLoading,
    error: charactersError,
  } = useCharacters(summaryId);

  let job: IJob | null = null;
  if (jobInstance) {
    job = serializeJob(jobInstance);
    if (factSheetWithoutCharacters) {
      job.manuscriptTitle = factSheetWithoutCharacters.title;
      job.manuscriptAuthor = factSheetWithoutCharacters.author;
    }
  }

  const summaryData: ISummaryData | null = factSheetWithoutCharacters
    ? new SummaryData({
        title: factSheetWithoutCharacters.title,
        author: factSheetWithoutCharacters.author,
        summary_paragraphs: paragraphs,
        synopsis: factSheetWithoutCharacters.synopsis,
        manuscript_word_count: factSheetWithoutCharacters.manuscript_word_count,
        genres: factSheetWithoutCharacters.genres,
        world_setting: factSheetWithoutCharacters.world_setting,
        characters: characters ?? undefined,
        tropes: factSheetWithoutCharacters.tropes,
      })
    : null;

  const areSubHooksInitializing =
    summaryId !== null &&
    !factSheetWithoutCharacters &&
    !factSheetWithoutCharactersError;
  const isLoading =
    jobLoading ||
    summaryLoading ||
    paragraphsLoading ||
    charactersLoading ||
    areSubHooksInitializing;
  const error =
    jobError ||
    factSheetWithoutCharactersError ||
    paragraphsError ||
    charactersError ||
    null;

  return { job, summaryData, isLoading, error };
};
